<template>
  <div>
    <div
      id="fullscreen"
      :class="computedClass"
      @click="onBackgroundClick"
      @dblclick="onDoubleClick"
      @contextmenu.prevent
    >
      <div
        class="image-frame"
        :style="imageFrameStyle"
        @contextmenu.prevent.stop="openContextMenu"
      >
        <img :key="image" :src="image" @load="onImageLoad" />
        <ImageFaceEditor
          v-if="faceEditing"
          ref="faceEditor"
          :media-id="currentMediaId"
          :frame-ready="Boolean(imageNaturalWidth)"
        />
      </div>
      <Icon class="control close" name="x" :size=24 @click.native.stop="close" />
      <div
        v-if="contextMenu.visible"
        ref="contextMenu"
        class="image-viewer-context-menu"
        :style="contextMenuStyle"
        role="menu"
        @click.stop
        @pointerdown.stop
        @contextmenu.prevent
      >
        <button
          type="button"
          class="face-menu-item"
          role="menuitemcheckbox"
          :aria-checked="String(faceEditing)"
          @click="toggleFaceEditing"
        >
          <Icon name="person-bounding-box" :size=11 />
          <span>{{ $t('people_faces_mode') }}</span>
          <Icon v-if="faceEditing" class="check" name="check" :size=11 />
        </button>
      </div>
      <Icon class="control prev" name="chevron-left" :size=24 v-if="!slideshowTimer" @click.native.stop="prev" />
      <Icon class="control next" name="chevron-right" :size=24 v-if="!slideshowTimer" @click.native.stop="next" />
      <Icon class="control wait" name="clock-history" :size=12 v-if="loading" />
    </div>
  </div>
</template>

<script>

import http from '../../js/http.js'
import ImageFaceEditor from './ImageFaceEditor.vue'

export default {
  components: { ImageFaceEditor },
  props: [ 'images', 'initialIndex', 'slideshow', 'loadMore' ],
  data: function() {
    return {
      index: this.initialIndex,
      galleryImages: [ ...(this.images || []) ],
      starting: false,
      loading: false,
      hovered: false,
      hoverTimer: null,
      slideshowTimer: null,
      rand: null,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      imageNaturalWidth: 0,
      imageNaturalHeight: 0,
      faceEditing: false,
      contextMenu: {
        visible: false,
        x: 0,
        y: 0,
      },
    }
  },
  computed: {
    currentImage() {
      return this.galleryImages[this.index] || null
    },
    image() {
      if (this.galleryImages.length == 0) {
        return `${http.defaults.baseURL || ''}/utils/randpic?_=${this.rand}`
      } else {
        return this.currentImage.url
      }
    },
    currentMediaId() {
      const rawId = this.currentImage?.uid ?? this.currentImage?.id?.split('-').pop()
      const mediaId = Number(rawId)
      return Number.isSafeInteger(mediaId) && mediaId > 0 ? mediaId : null
    },
    canEditFaces() {
      return !this.slideshowTimer && this.galleryImages.length > 0 && this.currentMediaId != null
    },
    contextMenuStyle() {
      return {
        left: `${this.contextMenu.x}px`,
        top: `${this.contextMenu.y}px`,
      }
    },
    imageFrameStyle() {
      if (!this.imageNaturalWidth || !this.imageNaturalHeight) {
        return { width: '100%', height: '100%' }
      }
      const scale = Math.min(
        this.viewportWidth / this.imageNaturalWidth,
        this.viewportHeight / this.imageNaturalHeight,
      )
      return {
        width: `${Math.round(this.imageNaturalWidth * scale)}px`,
        height: `${Math.round(this.imageNaturalHeight * scale)}px`,
      }
    },
    computedClass() {
      let classes = []
      if (this.hovered) {
        classes.push('hovered')
      }
      if (this.slideshowTimer) {
        classes.push('slideshow')
      }
      if (this.faceEditing) {
        classes.push('face-editing')
      }
      return classes
    },
  },
  watch: {
    index() {
      this.closeContextMenu()
      this.prepareCurrentImage()
    },
  },
  mounted: function() {
    
    // listsners
    document.addEventListener('mousemove', this.onMouseMove)
    document.addEventListener('keydown', this.onKeyDown)
    document.addEventListener('pointerdown', this.closeContextMenu)
    window.addEventListener('resize', this.onResize)

    // leave fullscreen
    document.addEventListener('fullscreenchange', this.exitFullscreen, false)
    document.addEventListener('mozfullscreenchange', this.exitFullscreen, false)
    document.addEventListener('MSFullscreenChange', this.exitFullscreen, false)
    document.addEventListener('webkitfullscreenchange', this.exitFullscreen, false)
    
    // mousemove triggered when entering fullscreen
    // so disable this for sometime...
    this.starting = true
    setTimeout(() => {
      this.starting = false
    }, 1000)

    // init
    this.hovered = false
    this.enterFullscreen()

    // automatic slideshow
    if (this.slideshow == true) {
      this.tick()
    }
    if (!this.canEditFaces) {
      this.faceEditing = false
    }
    
  },
  methods: {

    close() {

      // stop timer
      clearTimeout(this.slideshowTimer)
      this.slideshowTimer = null

      // remove all listeners
      document.removeEventListener('mousemove', this.onMouseMove)
      document.removeEventListener('keydown', this.onKeyDown)
      document.removeEventListener('pointerdown', this.closeContextMenu)
      window.removeEventListener('resize', this.onResize)
      document.removeEventListener('fullscreenchange', this.exitFullscreen)
      document.removeEventListener('mozfullscreenchange', this.exitFullscreen)
      document.removeEventListener('MSFullscreenChange', this.exitFullscreen)
      document.removeEventListener('webkitfullscreenchange', this.exitFullscreen)

      // now exit
      this.leaveFullscren()
      this.$router.back()

    },

    prev() {
      if (this.slideshowTimer != null) {
        this.tick()
      } else {
        this.loading = true
        this.index = (this.index == 0 ? this.galleryImages.length - 1 : this.index - 1)
      }
    },

    async next() {
      if (this.slideshowTimer != null) {
        this.tick()
      } else {
        this.loading = true
        if (this.index >= this.galleryImages.length - 10 && this.loadMore) {
          const newImages = await this.loadMore()
          if (Array.isArray(newImages)) {
            const loadedIds = new Set(this.galleryImages.map(image => image.id ?? image.url))
            this.galleryImages.push(...newImages.filter(image => !loadedIds.has(image.id ?? image.url)))
          }
        }
        this.index = (this.index + 1) % this.galleryImages.length
      }
    },

    tick() {
      clearTimeout(this.slideshowTimer)
      this.loading = true
      this.rand = Math.random()
      this.slideshowTimer = setTimeout(this.tick, 5000)
    },

    enterFullscreen() {
      let el = document.getElementById('fullscreen')
      if (el.requestFullScreen) {
        el.requestFullScreen()
      } else if (el.webkitRequestFullScreen) {
        el.webkitRequestFullScreen()
      } else if (el.mozRequestFullScreen) {
        el.mozRequestFullScreen()
      }
    },

    exitFullscreen() {
      if (!document.webkitIsFullScreen && !document.mozFullScreen && !document.msFullscreenElement) {
        this.close()
      }
    },

    leaveFullscren() {
      if (document.cancelFullScreen) {
        document.cancelFullScreen()
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen()
      } else if (document.webkitCancelFullScreen) {
        document.webkitCancelFullScreen()
      } else if (document.msCancelFullScreen) {
        document.msCancelFullScreen()
      }
    },

    onKeyDown(ev) {
      const typing = ['INPUT', 'SELECT', 'TEXTAREA'].includes(ev.target?.nodeName)
      if (typing && ev.keyCode !== 27) return
      if (ev.keyCode === 27 && this.contextMenu.visible) {
        this.closeContextMenu()
        return
      }
      if (ev.keyCode === 70) {
        this.toggleFaceEditing()
        return
      }
      switch (ev.keyCode) {
        case 13:
        case 32:
          this.close()
          break
        case 27:
          if (!this.$refs.faceEditor?.closeSelection()) {
            this.close()
          }
          break
        case 37:
          this.prev()
          break
        case 39:
          this.next()
          break
      }
    },

    onMouseMove() {
      if (!this.starting) {
        this.hovered = true
        clearTimeout(this.hoverTimer)
        this.hoverTimer = setTimeout(() => {
          this.hovered = false
        }, 3000)
      }
    },

    onResize() {
      this.viewportWidth = window.innerWidth
      this.viewportHeight = window.innerHeight
      this.closeContextMenu()
    },

    onImageLoad(event) {
      this.loading = false
      this.imageNaturalWidth = event.target.naturalWidth
      this.imageNaturalHeight = event.target.naturalHeight
    },

    onBackgroundClick() {
      if (this.faceEditing) {
        this.$refs.faceEditor?.closeSelection()
        return
      }
      this.next()
    },

    onDoubleClick() {
      if (!this.faceEditing) this.close()
    },

    prepareCurrentImage() {
      this.imageNaturalWidth = 0
      this.imageNaturalHeight = 0
    },

    openContextMenu(event) {
      if (!this.canEditFaces) return
      this.contextMenu = {
        visible: true,
        x: event.clientX,
        y: event.clientY,
      }
      this.$nextTick(this.boundContextMenu)
    },

    boundContextMenu() {
      const menu = this.$refs.contextMenu
      if (!menu) return
      const margin = 8
      this.contextMenu.x = Math.max(margin, Math.min(
        this.contextMenu.x,
        window.innerWidth - menu.offsetWidth - margin,
      ))
      this.contextMenu.y = Math.max(margin, Math.min(
        this.contextMenu.y,
        window.innerHeight - menu.offsetHeight - margin,
      ))
    },

    closeContextMenu() {
      this.contextMenu.visible = false
    },

    toggleFaceEditing() {
      if (!this.canEditFaces) return
      this.faceEditing = !this.faceEditing
      this.closeContextMenu()
    },

  }

}

</script>

<style scoped>

#fullscreen {
  background-color: black;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

#fullscreen .image-frame {
  position: relative;
  flex: 0 0 auto;
}

#fullscreen .control {
  background-color: black;
  border-radius: 8px;
  padding: 8px;
  opacity: 0;
  cursor: pointer;
}

#fullscreen.hovered .control {
  opacity: 0.5;
}

#fullscreen .control:hover {
  opacity: 0.7;
}

#fullscreen .close {
  position: absolute;
  top: 32px;
  right: 32px;
}

.image-viewer-context-menu {
  position: fixed;
  z-index: 1100;
  min-width: 12rem;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, .18);
  border-radius: 8px;
  background: #252525;
  box-shadow: 0 8px 24px rgba(0, 0, 0, .45);
}

.image-viewer-context-menu .face-menu-item {
  display: grid;
  width: 100%;
  grid-template-columns: auto 1fr 16px;
  gap: 8px;
  align-items: center;
  padding: .65rem .85rem;
  border: 0;
  background: transparent;
  color: var(--text-color);
  text-align: left;
}

.image-viewer-context-menu .face-menu-item:hover,
.image-viewer-context-menu .face-menu-item:focus {
  background: var(--highlight-color);
  color: white;
  outline: none;
}

.image-viewer-context-menu .face-menu-item .check {
  justify-self: end;
}

#fullscreen .wait {
  opacity: 0.5 !important;
  position: absolute;
  bottom: 32px;
  right: 32px;
}

#fullscreen .prev {
  position: absolute;
  top: 50%;
  left: 32px;
}

#fullscreen .next {
  position: absolute;
  top: 50%;
  right: 32px;
}

#fullscreen img {
  width: 100%;
  height: 100%;
  object-fit: fill;
  display: block;
}

#fullscreen.face-editing .prev,
#fullscreen.face-editing .next,
#fullscreen.face-editing .close {
  opacity: .65;
}

</style>
